import { useState, useRef, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

interface ImageUploadProps {
  currentUrl: string;
  onUpload: (url: string) => void;
  onDelete: () => void;
  bucket?: string;
}

export default function ImageUpload({ currentUrl, onUpload, onDelete, bucket = 'blog-images' }: ImageUploadProps) {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string>(currentUrl);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const generateFileName = (originalName: string) => {
    const ext = originalName.split('.').pop()?.toLowerCase() || 'jpg';
    const uuid = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    return `blog/${uuid}.${ext}`;
  };

  const uploadFile = async (file: File) => {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Only JPG, PNG, and WEBP files are supported.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be under 10MB.');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const fileName = generateFileName(file.name);
      const { error: uploadErr } = await supabase.storage.from(bucket).upload(fileName, file, {
        cacheControl: '3600',
        upsert: false,
      });

      if (uploadErr) throw uploadErr;

      const { data: urlData } = supabase.storage.from(bucket).getPublicUrl(fileName);
      const publicUrl = urlData.publicUrl;

      // Try to delete old image if it's from our bucket
      if (currentUrl && currentUrl.includes(bucket)) {
        try {
          const oldPath = currentUrl.split(`${bucket}/`)[1];
          if (oldPath) await supabase.storage.from(bucket).remove([oldPath]);
        } catch {
          // ignore cleanup errors
        }
      }

      setPreviewUrl(publicUrl);
      onUpload(publicUrl);
    } catch (err: any) {
      setError(err.message || 'Upload failed. Please try again.');
    }
    setUploading(false);
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) uploadFile(file);
  }, [currentUrl]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) uploadFile(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDelete = () => {
    setPreviewUrl('');
    onDelete();
  };

  return (
    <div className="space-y-3">
      {previewUrl ? (
        <div className="relative rounded-xl overflow-hidden bg-background-100 border border-background-200">
          <img
            src={previewUrl}
            alt="Featured"
            className="w-full aspect-video object-cover"
          />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
          <div className="absolute bottom-3 right-3 flex items-center gap-2">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="bg-white/90 backdrop-blur-sm text-foreground-800 hover:bg-white px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors cursor-pointer flex items-center gap-1.5 shadow-sm whitespace-nowrap"
            >
              <i className="ri-refresh-line"></i>
              Replace
            </button>
            <button
              type="button"
              onClick={handleDelete}
              disabled={uploading}
              className="bg-white/90 backdrop-blur-sm text-accent-600 hover:bg-white px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors cursor-pointer flex items-center gap-1.5 shadow-sm whitespace-nowrap"
            >
              <i className="ri-delete-bin-line"></i>
              Delete
            </button>
          </div>
        </div>
      ) : (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`relative border-2 border-dashed rounded-xl p-10 flex flex-col items-center justify-center cursor-pointer transition-colors ${
            dragOver
              ? 'border-primary-400 bg-primary-50'
              : 'border-background-300 hover:border-primary-300 hover:bg-background-50'
          }`}
        >
          {uploading ? (
            <div className="flex flex-col items-center gap-3">
              <div className="w-10 h-10 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-foreground-600 font-medium">Uploading...</p>
            </div>
          ) : (
            <>
              <div className="w-14 h-14 bg-background-100 rounded-full flex items-center justify-center mb-3">
                <i className="ri-image-add-line text-foreground-400 text-2xl"></i>
              </div>
              <p className="text-sm font-semibold text-foreground-700 mb-1">Drag image here</p>
              <p className="text-xs text-foreground-500 mb-4">or click to browse</p>
              <span className="text-[10px] text-foreground-400 bg-background-100 px-3 py-1 rounded-full font-medium">PNG · JPG · WEBP · Max 10MB</span>
            </>
          )}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleFileChange}
        className="hidden"
      />

      {error && (
        <div className="flex items-center gap-2 p-3 bg-accent-50 border border-accent-200 rounded-lg">
          <i className="ri-error-warning-line text-accent-500 text-sm"></i>
          <p className="text-xs text-accent-700">{error}</p>
        </div>
      )}
    </div>
  );
}